import React, { useCallback, useRef, useState } from 'react';
import {
  Upload,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Zap,
  Loader2,
  RotateCcw,
  BookOpen,
  PenTool,
  QrCode,
} from 'lucide-react';
import {
  useBatchUploadQRMutation,
  useLancerOCRCorrectionMutation,
  useLancerOCRDevoirsMutation,
  BatchUploadFichierAssocieQR,
  BatchUploadFichierNonIdentifie,
} from '../../services/ocrApi';

interface FichierLocal {
  fichier: File;
  nom: string;
  etat: 'attente' | 'traitement' | 'associe' | 'non_identifie';
  info?: BatchUploadFichierAssocieQR;
  raison?: string;
}

const UploadAutomatiqueQR: React.FC = () => {
  const [fichiers, setFichiers] = useState<FichierLocal[]>([]);
  const [lancerOCRAuto, setLancerOCRAuto] = useState(false);
  const [resultats, setResultats] = useState<{
    associes: BatchUploadFichierAssocieQR[];
    non_identifies: BatchUploadFichierNonIdentifie[];
    nb_associes: number;
    nb_non_identifies: number;
  } | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [ocrEnCours, setOcrEnCours] = useState<Set<number>>(new Set());

  const inputRef = useRef<HTMLInputElement>(null);

  const [batchUpload] = useBatchUploadQRMutation();
  const [lancerOCRCorrection] = useLancerOCRCorrectionMutation();
  const [lancerOCRDevoirs] = useLancerOCRDevoirsMutation();

  const ajouterFichiers = useCallback((nouveaux: File[]) => {
    const pdfs = nouveaux.filter(
      (f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')
    );
    const items: FichierLocal[] = pdfs.map((f) => ({
      fichier: f,
      nom: f.name,
      etat: 'attente',
    }));
    setFichiers((prev) => [...prev, ...items]);
    setResultats(null);
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const dropped = Array.from(e.dataTransfer.files);
      ajouterFichiers(dropped);
    },
    [ajouterFichiers]
  );

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      ajouterFichiers(Array.from(e.target.files));
      e.target.value = '';
    }
  };

  const retirerFichier = (index: number) => {
    setFichiers((prev) => prev.filter((_, i) => i !== index));
  };

  const analyser = async () => {
    if (fichiers.length === 0) return;
    setEnCours(true);
    setResultats(null);

    const formData = new FormData();
    fichiers.forEach((f) => formData.append('fichiers', f.fichier, f.nom));
    if (lancerOCRAuto) formData.append('lancer_ocr', 'true');

    try {
      const res = await batchUpload(formData).unwrap();
      setResultats(res);

      // Mettre à jour l'état local de chaque fichier
      setFichiers((prev) =>
        prev.map((f) => {
          const associe = res.associes.find((a) => a.fichier === f.nom);
          if (associe) return { ...f, etat: 'associe', info: associe };
          const nonId = res.non_identifies.find((n) => n.fichier === f.nom);
          if (nonId) return { ...f, etat: 'non_identifie', raison: nonId.raison };
          return f;
        })
      );
    } catch (err: any) {
      console.error('Erreur batch upload QR', err);
    } finally {
      setEnCours(false);
    }
  };

  const lancerOCR = async (releveId: number, type: string) => {
    setOcrEnCours((prev) => new Set(prev).add(releveId));
    try {
      if (type === 'correction') {
        await lancerOCRCorrection(releveId).unwrap();
      } else {
        await lancerOCRDevoirs(releveId).unwrap();
      }
    } catch (err) {
      console.error('Erreur lancement OCR', err);
    } finally {
      setOcrEnCours((prev) => {
        const next = new Set(prev);
        next.delete(releveId);
        return next;
      });
    }
  };

  const reinitialiser = () => {
    setFichiers([]);
    setResultats(null);
  };

  const nbAttente = fichiers.filter((f) => f.etat === 'attente').length;
  const corrections = resultats?.associes.filter((a) => a.type === 'correction') ?? [];
  const devoirs = resultats?.associes.filter((a) => a.type === 'devoirs') ?? [];

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* En-tête */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 rounded-lg dark:bg-blue-900/30">
          <QrCode className="text-blue-600 dark:text-blue-400" size={28} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            Upload Automatique par QR Code
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Déposez les scans (PDF) — le système identifie automatiquement chaque relevé grâce au QR
            code imprimé dessus (examens et devoirs).
          </p>
        </div>
      </div>

      {/* Zone de dépôt */}
      <div
        onDrop={onDrop}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
          isDragOver
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800'
        }`}
      >
        <Upload className="mx-auto mb-3 text-gray-400 dark:text-gray-500" size={40} />
        <p className="text-gray-600 dark:text-gray-300 font-medium">
          Glissez-déposez vos fichiers PDF ici
        </p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">ou cliquez pour parcourir</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,application/pdf"
          className="hidden"
          onChange={onInputChange}
        />
      </div>

      {/* Liste des fichiers sélectionnés */}
      {fichiers.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
          <div className="flex items-center justify-between px-4 py-3">
            <span className="font-medium text-gray-700 dark:text-gray-200">
              {fichiers.length} fichier{fichiers.length > 1 ? 's' : ''} sélectionné{fichiers.length > 1 ? 's' : ''}
            </span>
            <button
              onClick={reinitialiser}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500 dark:text-gray-400"
            >
              <RotateCcw size={13} /> Réinitialiser
            </button>
          </div>
          {fichiers.map((f, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-2.5">
              <FileText size={16} className="text-gray-400 flex-shrink-0" />
              <span className="flex-1 text-sm text-gray-700 dark:text-gray-200 truncate">{f.nom}</span>
              {/* Indicateur d'état */}
              {f.etat === 'attente' && (
                <span className="text-xs text-gray-400 dark:text-gray-500">En attente</span>
              )}
              {f.etat === 'associe' && (
                <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                  <CheckCircle size={13} />
                  {f.info?.type === 'correction' ? 'Examen' : 'Devoir'} — {f.info?.matiere} ({f.info?.niveau})
                </span>
              )}
              {f.etat === 'non_identifie' && (
                <span className="flex items-center gap-1 text-xs text-orange-500 dark:text-orange-400">
                  <AlertCircle size={13} /> Non identifié
                </span>
              )}
              {f.etat === 'attente' && (
                <button
                  onClick={(e) => { e.stopPropagation(); retirerFichier(i); }}
                  className="text-gray-400 hover:text-red-400 text-xs ml-2"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Options + bouton analyser */}
      {nbAttente > 0 && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={lancerOCRAuto}
              onChange={(e) => setLancerOCRAuto(e.target.checked)}
              className="w-4 h-4 rounded accent-blue-600"
            />
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Lancer l'OCR automatiquement après identification
            </span>
          </label>
          <button
            onClick={analyser}
            disabled={enCours}
            className="ml-auto flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-5 py-2.5 rounded-lg font-medium text-sm transition-colors"
          >
            {enCours ? (
              <><Loader2 size={16} className="animate-spin" /> Analyse en cours…</>
            ) : (
              <><QrCode size={16} /> Analyser et Associer ({nbAttente})</>
            )}
          </button>
        </div>
      )}

      {/* Résultats */}
      {resultats && (
        <div className="space-y-4">
          {/* Résumé */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 text-center border border-gray-200 dark:border-gray-700">
              <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{resultats.nb_associes + resultats.nb_non_identifies}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Total traités</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 text-center border border-green-200 dark:border-green-800">
              <p className="text-2xl font-bold text-green-700 dark:text-green-400">{resultats.nb_associes}</p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">Identifiés</p>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4 text-center border border-orange-200 dark:border-orange-800">
              <p className="text-2xl font-bold text-orange-700 dark:text-orange-400">{resultats.nb_non_identifies}</p>
              <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">Non identifiés</p>
            </div>
          </div>

          {/* Relevés de Correction (Examens) */}
          {corrections.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <BookOpen size={16} className="text-blue-600 dark:text-blue-400" />
                <h3 className="font-semibold text-gray-800 dark:text-gray-100">
                  Relevés Examens identifiés ({corrections.length})
                </h3>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {corrections.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3">
                    <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                        {item.fichier}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {item.matiere} — {item.niveau} — {item.semestre}
                        {item.option && ` — ${item.option}`}
                        {' '}
                        <span className="font-mono text-gray-400">{item.code_releve}</span>
                      </p>
                    </div>
                    {item.ocr_status === 'lance' || item.ocr_status === 'termine' ? (
                      <span className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                        <Zap size={12} /> {item.ocr_status === 'termine' ? 'OCR terminé' : 'OCR lancé'}
                      </span>
                    ) : (
                      <button
                        onClick={() => lancerOCR(item.releve_id, 'correction')}
                        disabled={ocrEnCours.has(item.releve_id)}
                        className="flex items-center gap-1 text-xs bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
                      >
                        {ocrEnCours.has(item.releve_id) ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Zap size={12} />
                        )}
                        Lancer OCR
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Relevés de Devoirs */}
          {devoirs.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <PenTool size={16} className="text-purple-600 dark:text-purple-400" />
                <h3 className="font-semibold text-gray-800 dark:text-gray-100">
                  Relevés Devoirs identifiés ({devoirs.length})
                </h3>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {devoirs.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3">
                    <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                        {item.fichier}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {item.matiere} — {item.niveau} — {item.semestre}
                        {item.option && ` — ${item.option}`}
                        {' '}
                        <span className="font-mono text-gray-400">{item.code_releve}</span>
                      </p>
                    </div>
                    {item.ocr_status === 'lance' || item.ocr_status === 'termine' ? (
                      <span className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400">
                        <Zap size={12} /> {item.ocr_status === 'termine' ? 'OCR terminé' : 'OCR lancé'}
                      </span>
                    ) : (
                      <button
                        onClick={() => lancerOCR(item.releve_id, 'devoirs')}
                        disabled={ocrEnCours.has(item.releve_id)}
                        className="flex items-center gap-1 text-xs bg-purple-50 hover:bg-purple-100 dark:bg-purple-900/30 dark:hover:bg-purple-900/50 text-purple-700 dark:text-purple-300 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
                      >
                        {ocrEnCours.has(item.releve_id) ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Zap size={12} />
                        )}
                        Lancer OCR
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fichiers non identifiés */}
          {resultats.non_identifies.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-orange-200 dark:border-orange-800">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-orange-100 dark:border-orange-800">
                <AlertCircle size={16} className="text-orange-500" />
                <h3 className="font-semibold text-gray-800 dark:text-gray-100">
                  Fichiers non identifiés ({resultats.non_identifies.length})
                </h3>
              </div>
              <div className="divide-y divide-orange-50 dark:divide-orange-900/20">
                {resultats.non_identifies.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 px-4 py-3">
                    <XCircle size={16} className="text-orange-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                        {item.fichier}
                      </p>
                      <p className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">{item.raison}</p>
                      {item.qr_data && Object.keys(item.qr_data).length > 0 && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 font-mono">
                          QR partiel : {JSON.stringify(item.qr_data)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-4 py-3 bg-orange-50 dark:bg-orange-900/10 rounded-b-xl text-xs text-orange-700 dark:text-orange-300">
                Ces fichiers n'ont pas de QR code lisible. Vérifiez que le relevé a bien été généré
                depuis ce système, ou utilisez l'upload manuel depuis la page du relevé concerné.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UploadAutomatiqueQR;
